import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { GlobeIcon } from "lucide-react";
import { clonePhaseLabel } from "./CloneSiteCard.js";
import { formatClonePlan } from "./clone-status.js";
import { cn } from "./cn.js";
import { siteCloneInFlight, type ChatSiteClone } from "./messages.js";
import { SiteFavicon, hostFromUrl } from "./SiteFavicon.js";

type Props = {
	active: boolean;
	clones: readonly ChatSiteClone[];
	/** First-run pointer: stay open until they click or the clone finishes. */
	coach?: boolean;
	onOpen: () => void;
};

function CloneProgressTip({
	clone,
	coach,
}: {
	clone: ChatSiteClone;
	coach: boolean;
}) {
	const host = hostFromUrl(clone.sourceUrl || clone.name);
	const progress = Math.max(0, Math.min(100, clone.progress || 0));
	const plan = formatClonePlan(clone);
	return (
		<div className="mc-sites-tip">
			{coach ? (
				<p className="mc-sites-coach-lede">
					Click here to check on your site’s progress.
				</p>
			) : null}
			<div className="mc-sites-tip-row">
				<SiteFavicon url={clone.sourceUrl || host} live />
				<div className="min-w-0">
					<p className="mc-sites-tip-title">{host || clone.name}</p>
					<p className="mc-sites-tip-phase">{clonePhaseLabel(clone)}</p>
					{plan ? <p className="mc-sites-tip-eta">{plan}</p> : null}
				</div>
			</div>
			<div
				className="mc-sites-tip-bar"
				role="progressbar"
				aria-valuenow={progress}
				aria-valuemin={0}
				aria-valuemax={100}
			>
				<span style={{ width: `${progress}%` }} />
			</div>
			<p className="mc-sites-tip-pct">{Math.round(progress)}%</p>
		</div>
	);
}

/** Sidebar Sites control — pops in while cloning, with a tutorial callout. */
export function SitesNavButton({
	active,
	clones,
	coach = false,
	onOpen,
}: Props) {
	const inFlight = clones.filter(siteCloneInFlight);
	const busy = inFlight.length > 0;
	const lead = inFlight[0];
	const progress = Math.max(0, Math.min(100, lead?.progress ?? 0));
	const radius = 9;
	const circ = 2 * Math.PI * radius;
	const offset = circ * (1 - progress / 100);

	const plan = lead ? formatClonePlan(lead) : null;
	const label = busy
		? `${clonePhaseLabel(lead!)} · ${Math.round(progress)}%${plan ? ` · ${plan}` : ""}`
		: "Websites";

	return (
		<Tooltip open={coach ? true : undefined} delayDuration={coach ? 0 : 200}>
			<TooltipTrigger asChild>
				<button
					type="button"
					className={cn(
						"mc-side-nav-item mc-sites-nav",
						busy && "mc-sites-nav--busy",
						coach && "mc-sites-nav--coach",
					)}
					aria-current={active ? "page" : undefined}
					aria-label={label}
					onClick={onOpen}
				>
					<span className="mc-sites-nav-icon" aria-hidden>
						{busy ? (
							<svg
								className="mc-sites-nav-ring"
								viewBox="0 0 24 24"
								width={22}
								height={22}
							>
								<circle
									cx="12"
									cy="12"
									r={radius}
									className="mc-sites-nav-ring-track"
								/>
								<circle
									cx="12"
									cy="12"
									r={radius}
									className="mc-sites-nav-ring-fill"
									strokeDasharray={circ}
									strokeDashoffset={offset}
								/>
							</svg>
						) : null}
						<GlobeIcon className="size-4" />
					</span>
					<span className="mc-side-text">Websites</span>
				</button>
			</TooltipTrigger>
			<TooltipContent
				side="right"
				sideOffset={12}
				className={
					busy || coach
						? "mc-sites-tip-pop mc-sites-coach-pop border-0 bg-mx-elevated p-0 text-mx-label"
						: undefined
				}
			>
				{busy && lead ? (
					<CloneProgressTip clone={lead} coach={coach} />
				) : (
					"Websites"
				)}
			</TooltipContent>
		</Tooltip>
	);
}
